import {useState, useEffect, useRef} from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { MessageSquare, Send, Plus, ChevronDown, ChevronUp, Upload, Image as ImageIcon, Trash2, AlertTriangle } from 'lucide-react';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { formatTime, formatDateTime } from '../utils/dateFormat';
import { WorkspaceShell } from '../components/layout';

interface UserChat {
  _id?: string;
  id: string;
  subject: string;
  status: 'active' | 'closed' | 'pending';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: 'billing' | 'technical' | 'general' | 'complaint' | 'feature_request';
  lastMessageAt: string;
  messageCount: number;
  isReadByUser: boolean;
  isReadByAdmin: boolean;
  admin?: {
    id: string;
    email: string;
  };
  createdAt: string;
}

interface ChatMessage {
  id: string;
  content: string;
  senderType: 'user' | 'admin';
  sender: {
    id: string;
    email: string;
  };
  isRead: boolean;
  createdAt: string;
  attachments?: {
    type: 'image' | 'file';
    url: string;
    filename: string;
    size?: number;
  }[];
}

import PageTitle from '../components/PageTitle';

export default function UserChats() {
  const { user } = useAuth();
  const { socket, isConnected, connectionStatus, joinAdminChat, leaveAdminChat, sendTypingStart, sendTypingStop } = useSocket();
  const { t } = useTranslation('settings');
  const [chats, setChats] = useState<UserChat[]>([]);
  const [selectedChat, setSelectedChat] = useState<UserChat | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; subject: string } | null>(null);
  const [isDeletingChat, setIsDeletingChat] = useState(false);
  const [createForm, setCreateForm] = useState({
    subject: '',
    category: 'general',
    priority: 'medium',
    initialMessage: ''
  });
  const [isTyping, setIsTyping] = useState(false);
  const [adminTyping, setAdminTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Socket event listeners for real-time updates
  useEffect(() => {
    if (!socket) return;

    const handleNewMessage = (messageData: any) => {
      console.log('UserChats: Received new message via socket:', messageData);
      const currentChatId = selectedChat ? String(selectedChat._id || selectedChat.id) : '';
      const incomingChatId = String(messageData.chatId || '');

      // Only append to the open thread; otherwise rely on chat list + toast/notification.
      if (currentChatId && incomingChatId && currentChatId === incomingChatId) {
        setMessages(prev => {
          const exists = prev.some(msg => msg.id === messageData.id);
          if (exists) return prev;

          return [
            ...prev,
            {
              id: messageData.id,
              content: messageData.content,
              senderType: messageData.senderType,
              sender: messageData.sender,
              isRead: messageData.isRead,
              createdAt: messageData.createdAt,
              attachments: messageData.attachments || [],
            },
          ];
        });

        // Scroll to bottom for new messages in the active chat
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else if (messageData.senderType === 'admin') {
        toast.success(t('user_chats.new_message'), { icon: '💬' });
      }

      // Update chat's last message time
      setChats(prev => prev.map(chat =>
        String(chat._id || chat.id) === String(messageData.chatId)
          ? { ...chat, lastMessageAt: messageData.createdAt, messageCount: chat.messageCount + 1 }
          : chat
      ));
    };

    const handleAdminTyping = (data: any) => {
      const currentChatId = selectedChat ? String(selectedChat._id || selectedChat.id) : '';
      const typingChatId = String(data?.chatId || '');
      if (!currentChatId || !typingChatId || currentChatId !== typingChatId) return;
      setAdminTyping(true);
    };

    const handleAdminStoppedTyping = (data: any) => {
      const currentChatId = selectedChat ? String(selectedChat._id || selectedChat.id) : '';
      const typingChatId = String(data?.chatId || '');
      if (!currentChatId || !typingChatId || currentChatId !== typingChatId) return;
      setAdminTyping(false);
    };

    const handleChatUpdated = (update: any) => {
      setChats(prev => prev.map(chat =>
        (chat._id || chat.id) === update.chatId
          ? { ...chat, ...update.updates }
          : chat
      ));

      // Update selected chat if it's the current one
      if (selectedChat && (selectedChat._id || selectedChat.id) === update.chatId) {
        setSelectedChat(prev => prev ? { ...prev, ...update.updates } : null);
      }
    };

    // Handle notifications (new messages from admin)
    const handleChatNotification = (data: any) => {
      console.log('UserChats: Received notification:', data);
      
      if (data.type === 'new_message' && data.message) {
        const currentChatId = selectedChat ? String(selectedChat._id || selectedChat.id) : '';
        const notificationChatId = String(data.chatId || '');

        // If we're viewing this chat, add the message directly
        if (currentChatId && notificationChatId && currentChatId === notificationChatId) {
          setMessages(prev => {
            const exists = prev.some(msg => msg.id === data.message.id);
            if (exists) return prev;
            return [...prev, {
              id: data.message.id,
              content: data.message.content,
              senderType: data.message.senderType,
              sender: data.message.sender,
              isRead: data.message.isRead,
              createdAt: data.message.createdAt,
              attachments: data.message.attachments || []
            }];
          });
          
          // Scroll to bottom
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        } else if (data.message.senderType === 'admin') {
          // Show toast for new admin messages when not viewing that chat
          toast.success(t('user_chats.new_message'), { icon: '💬' });
        }
        
        // Update chat list
        setChats(prev => prev.map(chat =>
          String(chat._id || chat.id) === String(data.chatId)
            ? { 
                ...chat, 
                lastMessageAt: data.message.createdAt, 
                messageCount: chat.messageCount + 1,
                isReadByUser: false
              }
            : chat
        ));
      }
      
      if (data.type === 'chat_deleted') {
        // If server notifies deletion (e.g., admin deleted), refresh list and clear view if needed
        loadChats();
        if (selectedChat && String(selectedChat._id || selectedChat.id) === String(data.chatId)) {
          setSelectedChat(null);
          setMessages([]);
        }
      }
    };

    socket.on('admin-message-received', handleNewMessage);
    socket.on('admin-user-typing', handleAdminTyping);
    socket.on('admin-user-stopped-typing', handleAdminStoppedTyping);
    socket.on('admin-chat-updated', handleChatUpdated);
    socket.on('admin-chat-notification', handleChatNotification);

    return () => {
      socket.off('admin-message-received', handleNewMessage);
      socket.off('admin-user-typing', handleAdminTyping);
      socket.off('admin-user-stopped-typing', handleAdminStoppedTyping);
      socket.off('admin-chat-updated', handleChatUpdated);
      socket.off('admin-chat-notification', handleChatNotification);
    };
  }, [socket, selectedChat]);

  // Join/leave chat rooms when selectedChat changes
  useEffect(() => {
    if (selectedChat && isConnected) {
      joinAdminChat(selectedChat._id || selectedChat.id);
    }

    return () => {
      if (selectedChat && isConnected) {
        leaveAdminChat(selectedChat._id || selectedChat.id);
      }
    };
  }, [selectedChat, isConnected, joinAdminChat, leaveAdminChat]);

  // Auto-refresh messages every 10 seconds as fallback for socket issues
  useEffect(() => {
    if (!selectedChat) return;

    const interval = setInterval(() => {
      loadMessages(selectedChat._id || selectedChat.id);
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [selectedChat]);

  // Load user's admin chats
  const loadChats = async () => {
    try {
      const response = await axios.get('/api/admin-chats');
      setChats(response.data.chats);
    } catch (error) {
      console.error('Failed to load chats:', error);
      toast.error(t('user_chats.load_failed'));
    }
  };

  // Load messages for selected chat
  const loadMessages = async (chatId: string) => {
    try {
      const response = await axios.get(`/api/admin-chats/${chatId}`);
      setMessages(response.data.messages);
      setSelectedChat(response.data.chat);
    } catch (error) {
      console.error('Failed to load messages:', error);
      toast.error(t('user_chats.load_messages_failed'));
    }
  };

  // Send message
  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.type.startsWith('image/')) {
        validFiles.push(file);
      }
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      toast.success(t('user_chats.images_selected', { count: validFiles.length }));
    }
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadImages = async (): Promise<string[]> => {
    if (selectedFiles.length === 0) return [];

    setUploadingImages(true);
    const uploadedUrls: string[] = [];

    try {
      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post('/api/upload/chat-image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        uploadedUrls.push(response.data.url);
      }

      return uploadedUrls;
    } catch (error) {
      console.error('Failed to upload images:', error);
      toast.error(t('user_chats.upload_failed'));
      throw error;
    } finally {
      setUploadingImages(false);
    }
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && selectedFiles.length === 0) || !selectedChat) return;

    // Stop typing indicator
    if (isTyping) {
      sendTypingStop(selectedChat._id || selectedChat.id);
      setIsTyping(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    }

    setSending(true);
    try {
      let attachmentUrls: string[] = [];

      if (selectedFiles.length > 0) {
        attachmentUrls = await uploadImages();
      }

      const messageData: any = {
        content: newMessage.trim() || t('user_chats.sent_images')
      };

      if (attachmentUrls.length > 0) {
        messageData.attachments = attachmentUrls.map(url => ({
          type: 'image',
          url,
          filename: 'image.jpg' // This could be improved to get actual filename
        }));
      }

      await axios.post(`/api/admin-chats/${selectedChat._id || selectedChat.id}/messages`, messageData);

      setNewMessage('');
      setSelectedFiles([]);
      // Note: Real-time updates will handle message display and chat list updates
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(t('user_chats.send_failed'));
    } finally {
      setSending(false);
    }
  };

  // Handle input changes with typing indicators
  const handleInputChange = (value: string) => {
    setNewMessage(value);

    if (!selectedChat) return;

    // Send typing update (no content)
    sendTypingStart(selectedChat._id || selectedChat.id);
    setIsTyping(true);

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      if (isTyping) {
        sendTypingStop(selectedChat._id || selectedChat.id);
        setIsTyping(false);
      }
    }, 1000); // Stop typing after 1 second of inactivity
  };

  // Create new chat
  const createChat = async () => {
    if (!createForm.subject.trim() || !createForm.initialMessage.trim()) {
      toast.error(t('user_chats.fill_fields'));
      return;
    }

    try {
      await axios.post('/api/admin-chats', {
        subject: createForm.subject.trim(),
        category: createForm.category,
        priority: createForm.priority,
        initialMessage: createForm.initialMessage.trim()
      });

      toast.success(t('user_chats.created_success'));
      setShowCreateForm(false);
      setCreateForm({
        subject: '',
        category: 'general',
        priority: 'medium',
        initialMessage: ''
      });
      await loadChats();
    } catch (error) {
      console.error('Failed to create chat:', error);
      toast.error(t('user_chats.create_failed'));
    }
  };

  // Close chat
  const closeChat = async (chatId: string) => {
    try {
      await axios.patch(`/api/admin-chats/${chatId}/close`);
      toast.success(t('user_chats.chat_closed'));
      await loadChats();
      if (selectedChat?.id === chatId) {
        setSelectedChat(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to close chat:', error);
      toast.error(t('user_chats.close_failed'));
    }
  };

  const openDeleteDialog = (chat: UserChat) => {
    setDeleteTarget({ id: String(chat._id || chat.id), subject: chat.subject });
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (isDeletingChat) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const confirmDeleteChat = async () => {
    if (!deleteTarget || isDeletingChat) return;
    setIsDeletingChat(true);
    try {
      await axios.delete(`/api/admin-chats/${deleteTarget.id}`);
      toast.success(t('user_chats.chat_deleted'));
      await loadChats();
      if (selectedChat && String(selectedChat._id || selectedChat.id) === String(deleteTarget.id)) {
        setSelectedChat(null);
        setMessages([]);
      }
      closeDeleteDialog();
    } catch (error: any) {
      console.error('Failed to delete chat:', error);
      toast.error(error.response?.data?.error || t('user_chats.delete_failed'));
    } finally {
      setIsDeletingChat(false);
    }
  };

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    loadChats();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-muted text-foreground';
      default: return 'bg-muted text-foreground';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-muted text-foreground';
    }
  };

  const formatChatTime = (dateString: string) => {
    const diffInHours = (Date.now() - new Date(dateString).getTime()) / (1000 * 60 * 60);
    return diffInHours < 24 ? formatTime(dateString) : formatDateTime(dateString);
  };

  return (
    <WorkspaceShell
      title={t('user_chats.title')}
      actions={(
        <button
          onClick={() => setShowCreateForm(true)}
          className="btn btn-primary px-4 py-2 gap-2"
        >
          <Plus className="h-4 w-4" />
          <span>{t('user_chats.new_chat')}</span>
        </button>
      )}
    >
      <PageTitle 
        titleKey="user_chats.title" 
        namespace="settings" 
      />
      <div className="w-full">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-[calc(100vh-13rem)]">
          {/* Chat List */}
          <div className="bg-card rounded-xl shadow-sm border border-border flex flex-col">
            <div className="p-4 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">{t('user_chats.your_chats')}</h2>
            </div>
            <div className="flex-1 overflow-y-auto">
              {chats.map((chat) => (
                <div
                  key={chat._id || chat.id}
                  onClick={() => loadMessages(chat._id || chat.id)}
                  className={`p-4 border-b border-border cursor-pointer hover:bg-muted/50 transition-colors ${
                    (selectedChat?._id || selectedChat?.id) === (chat._id || chat.id) ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium text-foreground truncate flex-1 mr-2">
                      {chat.subject}
                    </h3>
                    {!chat.isReadByUser && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(chat.status)}`}>
                      {t(`user_chats.status_${chat.status}`)}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(chat.priority)}`}>
                      {t(`user_chats.priority_${chat.priority}`)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>{chat.messageCount} {t('user_chats.messages_count')}</span>
                    <span>{formatChatTime(chat.lastMessageAt)}</span>
                  </div>

                  {chat.admin && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      {t('user_chats.assigned_to')} {chat.admin.email}
                    </div>
                  )}
                </div>
              ))}

              {chats.length === 0 && (
                <div className="p-8 text-center text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="mb-4">{t('user_chats.no_chats')}</p>
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="btn btn-primary px-4 py-2"
                  >
                    {t('user_chats.start_first')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Chat Interface */}
          <div className="lg:col-span-2 bg-card rounded-xl shadow-sm border border-border flex flex-col">
            {selectedChat ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-border bg-muted/50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-foreground">
                          {selectedChat.subject}
                        </h3>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            connectionStatus === 'connected' ? 'bg-green-500' :
                            connectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                            connectionStatus === 'error' ? 'bg-red-500' : 'bg-gray-500'
                          }`} />
                          <span className="text-xs text-muted-foreground capitalize">
                            {t(`user_chats.connection_${connectionStatus}`)}
                          </span>
                        </div>
                        <button
                          onClick={() => setIsCollapsed(!isCollapsed)}
                          className="p-1 hover:bg-muted rounded transition-colors"
                        >
                          {isCollapsed ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(selectedChat.status)}`}>
                          {t(`user_chats.status_${selectedChat.status}`)}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {user?.email} ↔ {selectedChat.admin ? selectedChat.admin.email : t('user_chats.default_support_email')}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedChat.status !== 'closed' && (
                        <button
                          onClick={() => closeChat(selectedChat._id || selectedChat.id)}
                          className="px-3 py-1.5 bg-destructive text-destructive-foreground text-sm rounded-lg hover:bg-destructive/90 transition-colors"
                        >
                          {t('user_chats.close_chat')}
                        </button>
                      )}
                      <button
                        onClick={() => openDeleteDialog(selectedChat)}
                        className="px-3 py-1 border border-destructive/30 text-destructive bg-card hover:bg-destructive/10 text-sm rounded transition-colors flex items-center gap-2"
                        title={t('user_chats.delete_chat_btn')}
                      >
                        <Trash2 className="h-4 w-4" />
                        {t('user_chats.delete')}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Messages */}
                {!isCollapsed && (
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.senderType === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className="max-w-xs lg:max-w-md">
                          <div className="text-xs text-muted-foreground mb-1">
                            {message.sender.email}
                          </div>
                          <div
                            className={`px-4 py-2 rounded-lg ${
                              message.senderType === 'user'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted text-foreground'
                            }`}
                          >
                            {message.attachments && message.attachments.length > 0 && (
                              <div className="mb-2 space-y-2">
                                {message.attachments.map((attachment, index) => (
                                  <div key={index}>
                                    {attachment.type === 'image' ? (
                                      <img
                                        src={attachment.url}
                                        alt={attachment.filename}
                                        className="max-w-full h-auto rounded cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => window.open(attachment.url, '_blank')}
                                      />
                                    ) : (
                                      <div className="flex items-center gap-2 p-2 bg-muted rounded">
                                        <Upload className="h-4 w-4" />
                                        <span className="text-sm">{attachment.filename}</span>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            {message.content && <p className="text-sm">{message.content}</p>}
                            <p className={`text-xs mt-1 ${
                              message.senderType === 'user' ? 'text-primary-foreground/80' : 'text-muted-foreground'
                            }`}>
                              {formatChatTime(message.createdAt)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}

                {/* Typing Indicator */}
                {!isCollapsed && adminTyping && (
                  <div className="px-4 py-2 text-sm text-muted-foreground italic">
                    {t('user_chats.admin_typing')}
                  </div>
                )}

                {/* Message Input */}
                {!isCollapsed && selectedChat.status !== 'closed' && (
                  <div className="border-t border-border bg-muted/50">
                    {/* Selected Files Preview */}
                    {selectedFiles.length > 0 && (
                      <div className="p-4 border-b border-border">
                        <div className="flex flex-wrap gap-2">
                          {selectedFiles.map((file, index) => (
                            <div key={index} className="relative">
                              <div className="flex items-center gap-2 p-2 bg-primary/10 rounded-lg">
                                <ImageIcon className="h-4 w-4 text-primary" />
                                <span className="text-sm text-foreground">{file.name}</span>
                                <button
                                  onClick={() => removeSelectedFile(index)}
                                  className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="p-4">
                      <div className="flex gap-2">
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={(e) => handleFileSelect(e.target.files)}
                          className="hidden"
                          id="image-upload"
                        />
                        <label
                          htmlFor="image-upload"
                          className="p-2 text-muted-foreground hover:text-foreground cursor-pointer rounded-lg hover:bg-muted transition-colors"
                        >
                          <ImageIcon className="h-5 w-5" />
                        </label>
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => handleInputChange(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && !sending && !uploadingImages && sendMessage()}
                          placeholder={t('user_chats.type_message')}
                          className="flex-1 px-4 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                          disabled={sending || uploadingImages}
                        />
                        <button
                          onClick={sendMessage}
                          disabled={(!newMessage.trim() && selectedFiles.length === 0) || sending || uploadingImages}
                          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                          {sending || uploadingImages ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2">{t('user_chats.select_chat')}</h3>
                  <p>{t('user_chats.select_chat_desc')}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Create Chat Modal */}
        {showCreateForm && (
          <div 
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onKeyDown={e => e.key === 'Escape' && setShowCreateForm(false)}
            tabIndex={-1}
          >
            <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4 shadow-lg">
              <h3 className="text-lg font-semibold text-foreground mb-4">{t('user_chats.create_title')}</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('user_chats.subject')}
                  </label>
                  <input
                    type="text"
                    value={createForm.subject}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, subject: e.target.value }))}
                    className="input"
                    placeholder={t('user_chats.subject_placeholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('user_chats.category')}
                  </label>
                  <select
                    value={createForm.category}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, category: e.target.value }))}
                    className="input"
                  >
                    <option value="general">{t('user_chats.cat_general')}</option>
                    <option value="technical">{t('user_chats.cat_technical')}</option>
                    <option value="complaint">{t('user_chats.cat_complaint')}</option>
                    <option value="feature_request">{t('user_chats.cat_feature')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('user_chats.priority')}
                  </label>
                  <select
                    value={createForm.priority}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, priority: e.target.value }))}
                    className="input"
                  >
                    <option value="low">{t('user_chats.pri_low')}</option>
                    <option value="medium">{t('user_chats.pri_medium')}</option>
                    <option value="high">{t('user_chats.pri_high')}</option>
                    <option value="urgent">{t('user_chats.pri_urgent')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t('user_chats.initial_message')}
                  </label>
                  <textarea
                    value={createForm.initialMessage}
                    onChange={(e) => setCreateForm(prev => ({ ...prev, initialMessage: e.target.value }))}
                    className="input h-24 resize-none"
                    placeholder={t('user_chats.initial_placeholder')}
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                >
                  {t('user_chats.cancel')}
                </button>
                <button
                  onClick={createChat}
                  className="flex-1 btn btn-primary px-4 py-2"
                >
                  {t('user_chats.create_chat')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete confirmation modal */}
        {deleteDialogOpen && deleteTarget && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
            role="dialog"
            aria-modal="true"
            onKeyDown={e => e.key === 'Escape' && closeDeleteDialog()}
            tabIndex={-1}
          >
            <div className="w-full max-w-md rounded-lg bg-card shadow-lg border border-border overflow-hidden">
              <div className="p-6">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-foreground">{t('user_chats.delete_title')}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t('user_chats.delete_confirm', { subject: deleteTarget.subject })}
                    </p>
                    <p className="mt-2 text-sm text-muted-foreground">{t('user_chats.cannot_undone')}</p>
                  </div>
                </div>
              </div>

              <div className="px-6 pb-6 flex items-center justify-end gap-2">
                <button
                  onClick={closeDeleteDialog}
                  disabled={isDeletingChat}
                  className="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-muted/50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {t('user_chats.cancel')}
                </button>
                <button
                  onClick={confirmDeleteChat}
                  disabled={isDeletingChat}
                  className="px-4 py-2 rounded-lg bg-destructive hover:bg-destructive/90 text-destructive-foreground font-medium disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {isDeletingChat && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  {t('user_chats.delete_chat_btn')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </WorkspaceShell>
  );
}
